'use client';

import { useEffect, useState, useCallback } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { Box, keyframes, alpha } from '@mui/material';

// Top progress bar animation
const progressAnimation = keyframes`
  0% {
    width: 0%;
    opacity: 1;
  }
  50% {
    width: 70%;
    opacity: 1;
  }
  100% {
    width: 100%;
    opacity: 0;
  }
`;

const shimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
`;

/**
 * Global route progress indicator - shows loading state during navigation
 * Appears as a thin progress bar at the top of the screen
 */
export function RouteProgress({ color = '#6366f1' }) {
  // Only run on client side to avoid SSR issues with useSearchParams
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) return null;

  return <RouteProgressInner color={color} />;
}

function RouteProgressInner({ color }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  // Start loading when route changes are detected
  useEffect(() => {
    let timeout;
    let progressInterval;

    // Show loading state
    setIsLoading(true);
    setIsVisible(true);
    setProgress(0);

    // Animate progress
    progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        // Slow down as we get closer to completion
        const increment = Math.max(1, (90 - prev) / 10);
        return Math.min(90, prev + increment);
      });
    }, 100);

    // Complete the loading after content loads
    timeout = setTimeout(() => {
      setProgress(100);
      clearInterval(progressInterval);

      // Hide after completion animation
      setTimeout(() => {
        setIsLoading(false);
        setIsVisible(false);
        setProgress(0);
      }, 300);
    }, 400);

    return () => {
      clearTimeout(timeout);
      clearInterval(progressInterval);
    };
  }, [pathname, searchParams]);

  if (!isVisible) return null;

  return (
    <>
      {/* Top progress bar */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          zIndex: 9999,
          bgcolor: alpha(color, 0.1),
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${progress}%`,
            background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.8)}, ${color})`,
            borderRadius: '0 4px 4px 0',
            transition: 'width 0.2s ease-out',
            position: 'relative',
            boxShadow: `0 0 10px ${alpha(color, 0.5)}, 0 0 5px ${alpha(color, 0.3)}`,
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: 100,
              height: '100%',
              background: `linear-gradient(90deg, transparent, ${alpha('#fff', 0.4)}, transparent)`,
              animation: `${shimmer} 1s infinite`,
            },
          }}
        />
      </Box>

      {/* Optional: subtle overlay for perceived loading */}
      {isLoading && progress < 90 && (
        <Box
          sx={{
            position: 'fixed',
            top: 3,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9990,
            bgcolor: alpha('#fff', 0.02),
            pointerEvents: 'none',
            opacity: isLoading ? 1 : 0,
            transition: 'opacity 0.2s ease-out',
          }}
        />
      )}
    </>
  );
}

/**
 * Loading dots indicator for inline use
 */
export function LoadingDots({ color = '#6366f1', size = 8 }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: size,
            height: size,
            borderRadius: '50%',
            bgcolor: color,
            animation: `${pulse} 1.4s ease-in-out infinite`,
            animationDelay: `${i * 0.16}s`,
          }}
        />
      ))}
    </Box>
  );
}

/**
 * Skeleton shimmer component for content loading
 */
export function SkeletonShimmer({ width = '100%', height = 20, borderRadius = 4 }) {
  return (
    <Box
      sx={{
        width,
        height,
        borderRadius: `${borderRadius}px`,
        bgcolor: '#e2e8f0',
        position: 'relative',
        overflow: 'hidden',
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
          animation: `${shimmer} 1.5s infinite`,
        },
      }}
    />
  );
}

export default RouteProgress;
